import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { RadioChangeEvent } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listServiceProviderDepositPayments,
  type ServiceProviderDepositPaymentItem,
  type ServiceProviderDepositPaymentStatus,
} from '../../services/serviceProviderDepositPaymentService';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface SearchFormValues {
  keyword?: string;
  status?: ServiceProviderDepositPaymentStatus | 'all';
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
}

const statusOptions = [
  { label: '全部', value: 'all' },
  { label: '待支付', value: 'pending' },
  { label: '已支付', value: 'paid' },
  { label: '支付失败', value: 'failed' },
];

const statusMeta: Record<string, { color: string; text: string }> = {
  pending: { color: 'processing', text: '待支付' },
  paid: { color: 'success', text: '已支付' },
  failed: { color: 'error', text: '支付失败' },
};

const moneyFormatter = (value?: number) =>
  `￥${Number(value || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const renderStatus = (status: string, fallback?: string) => {
  const meta = statusMeta[status] || { color: 'default', text: fallback || status || '-' };
  return <Tag color={meta.color}>{meta.text}</Tag>;
};

const StaffDepositPayment: React.FC = () => {
  const [searchForm] = Form.useForm<SearchFormValues>();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ServiceProviderDepositPaymentItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [currentDetail, setCurrentDetail] = useState<ServiceProviderDepositPaymentItem | null>(null);
  const tableWrapRef = useHorizontalTableWheel();

  const loadData = useCallback(
    async (page = pagination.page, pageSize = pagination.pageSize) => {
      setLoading(true);
      try {
        const values = searchForm.getFieldsValue();
        const response = await listServiceProviderDepositPayments({
          page,
          pageSize,
          keyword: values.keyword || undefined,
          status: values.status || 'all',
          startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
        });
        setItems(response.list || []);
        setPagination({
          page,
          pageSize,
          total: response.total || 0,
        });
      } catch {
        message.error('加载保证金支付流水失败');
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, pagination.pageSize, searchForm],
  );

  useEffect(() => {
    void loadData(1, 10);
  }, [loadData]);

  const handleSearch = () => {
    void loadData(1, pagination.pageSize);
  };

  const handleStatusChange = (event: RadioChangeEvent) => {
    searchForm.setFieldsValue({ status: event.target.value || 'all' });
    void loadData(1, pagination.pageSize);
  };

  const handleReset = () => {
    searchForm.setFieldsValue({
      keyword: undefined,
      status: 'all',
      dateRange: undefined,
    });
    void loadData(1, pagination.pageSize);
  };

  const openDetail = (record: ServiceProviderDepositPaymentItem) => {
    setCurrentDetail(record);
    setDetailOpen(true);
  };

  const columns: ColumnsType<ServiceProviderDepositPaymentItem> = [
    {
      title: '服务人员',
      dataIndex: 'providerName',
      width: 180,
      render: (_, record) => (
        <div>
          <div>{record.providerName || '-'}</div>
          <Text type="secondary">{record.providerPhone || '-'}</Text>
        </div>
      ),
    },
    {
      title: '支付单号',
      dataIndex: 'paymentNo',
      width: 220,
      render: (value: string) => value || '-',
    },
    {
      title: '保证金金额',
      dataIndex: 'amount',
      width: 130,
      render: (amount: number) => <Text strong type="danger">{moneyFormatter(amount)}</Text>,
    },
    {
      title: '支付状态',
      dataIndex: 'status',
      width: 110,
      render: (status: string, record) => renderStatus(status, record.statusText),
    },
    {
      title: '支付渠道',
      dataIndex: 'payChannel',
      width: 100,
      render: (value: string) => (value === 'wechat' ? '微信支付' : value || '-'),
    },
    {
      title: '微信交易号',
      dataIndex: 'tradeNo',
      width: 220,
      render: (value: string) => value || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '支付时间',
      dataIndex: 'paidAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '备注',
      dataIndex: 'remark',
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={(
          <Space>
            <WalletOutlined />
            <span>保证金支付流水</span>
          </Space>
        )}
        extra={(
          <Button icon={<ReloadOutlined />} onClick={() => void loadData()}>
            刷新
          </Button>
        )}
      >
        <Form
          form={searchForm}
          layout="inline"
          initialValues={{ status: 'all' }}
          style={{ marginBottom: 16, rowGap: 12 }}
        >
          <Form.Item name="keyword" label="关键词">
            <Input allowClear placeholder="姓名 / 手机号 / 支付单号 / 交易号" style={{ width: 300 }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={statusOptions}
              onChange={handleStatusChange}
            />
          </Form.Item>
          <Form.Item name="dateRange" label="支付时间">
            <RangePicker allowClear />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <div ref={tableWrapRef}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={items}
            scroll={{ x: 1500 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: total => `共 ${total} 条`,
              onChange: (page, pageSize) => void loadData(page, pageSize),
            }}
          />
        </div>
      </Card>

      <Drawer title="保证金支付详情" open={detailOpen} onClose={() => setDetailOpen(false)} size={720}>
        {currentDetail && (
          <Card size="small">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="服务人员">{currentDetail.providerName || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{currentDetail.providerPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="支付单号">{currentDetail.paymentNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="保证金金额">{moneyFormatter(currentDetail.amount)}</Descriptions.Item>
              <Descriptions.Item label="支付状态">
                {renderStatus(currentDetail.status, currentDetail.statusText)}
              </Descriptions.Item>
              <Descriptions.Item label="支付渠道">
                {currentDetail.payChannel === 'wechat' ? '微信支付' : currentDetail.payChannel || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="微信交易号" span={2}>{currentDetail.tradeNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="预支付ID" span={2}>{currentDetail.prepayId || '-'}</Descriptions.Item>
              <Descriptions.Item label="付款OpenID" span={2}>{currentDetail.payerOpenId || '-'}</Descriptions.Item>
              <Descriptions.Item label="回调地址" span={2}>{currentDetail.notifyUrl || '-'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{currentDetail.createdAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="支付时间">{currentDetail.paidAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="更新时间" span={2}>{currentDetail.updatedAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="备注" span={2}>{currentDetail.remark || '-'}</Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Drawer>
    </div>
  );
};

export default StaffDepositPayment;
